/**
 * Entrada na plataforma.
 *
 * O cadastro pede data de nascimento e CPF na mesma tela. Antes eram um
 * segundo passo, e o motivo de terem saído de lá não é fluidez: quem se
 * cadastrava chegava ao lobby sem poder apostar valor real, descobria isso
 * ao tentar, e o pedido de documento aparecia como obstáculo surpresa
 * exatamente no momento em que a pessoa queria jogar.
 *
 * O que a tela diz sobre esses dados continua sendo a verdade: são
 * declarados, não verificados. A conta nasce com identidade `PENDING`, e
 * quem move para verificado é uma pessoa que olhou documento. Chamar isto
 * de verificação daria a impressão de que alguém conferiu algo.
 *
 * Quem entra pelo Google não passa por formulário nenhum e chega sem esses
 * dados — por isso a coleta continua existindo dentro do aplicativo, na
 * Carteira. Este formulário a antecipa; não a substitui.
 */
import { type FormEvent, useState } from 'react';

import { ArrowLeft, Eye, EyeOff } from 'lucide-react';

import { PainelTabuleiro } from '../components/PainelTabuleiro.tsx';
import { formatarCpf } from '../lib/carteira.ts';
import { cadastrar, entrar, entrarComGoogle } from '../lib/sessao.ts';

type Modo = 'entrar' | 'cadastrar';

/** Dígitos verificadores. Prova aritmética, não prova de quem digitou. */
function cpfValido(bruto: string): boolean {
  const d = bruto.replace(/\D/g, '');
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;

  const digito = (ate: number): number => {
    let soma = 0;
    for (let i = 0; i < ate; i += 1) soma += Number(d[i]) * (ate + 1 - i);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };

  return digito(9) === Number(d[9]) && digito(10) === Number(d[10]);
}

/** Data em que a pessoa completa 18 anos hoje: o máximo aceito no campo. */
function maiorIdadeEm(): string {
  const hoje = new Date();
  const limite = new Date(hoje.getFullYear() - 18, hoje.getMonth(), hoje.getDate());
  return limite.toISOString().slice(0, 10);
}

function GoogleIcone() {
  return (
    <svg viewBox="0 0 24 24" className="size-5" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.2-4 1.2-3.1 0-5.7-2.1-6.6-4.9H1.4v3.1A12 12 0 0 0 12 24z"
      />
      <path fill="#FBBC05" d="M5.4 14.4a7.2 7.2 0 0 1 0-4.6V6.7H1.4a12 12 0 0 0 0 10.6z" />
      <path
        fill="#EA4335"
        d="M12 4.8c1.8 0 3.4.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.4 6.7l4 3.1C6.3 6.9 8.9 4.8 12 4.8z"
      />
    </svg>
  );
}

export function Entrar({ onVoltar }: { onVoltar: () => void }) {
  const [modo, setModo] = useState<Modo>('entrar');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [nome, setNome] = useState('');
  const [nascimento, setNascimento] = useState('');
  const [cpf, setCpf] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const cadastrando = modo === 'cadastrar';

  // Conferido aqui só para a pessoa saber antes de enviar. Quem decide é
  // o banco: `fn_cpf_valido` roda a mesma conta, e é a dele que vale.
  const cpfDigitado = cpf.replace(/\D/g, '');
  const cpfErrado = cpfDigitado.length === 11 && !cpfValido(cpfDigitado);

  const podeEnviar =
    !enviando &&
    email.trim() !== '' &&
    senha.length >= 6 &&
    (!cadastrando ||
      (nome.trim().length >= 2 && nascimento !== '' && cpfDigitado.length === 11 && !cpfErrado));

  async function enviarFormulario(evento: FormEvent): Promise<void> {
    evento.preventDefault();
    setEnviando(true);
    setErro(null);
    setAviso(null);

    const resultado = cadastrando
      ? await cadastrar(email, senha, { nome, nascimento, cpf })
      : await entrar(email, senha);

    setEnviando(false);
    if (!resultado.ok) {
      setErro(resultado.mensagem ?? 'Não foi possível continuar.');
      return;
    }
    if (resultado.precisaConfirmar) {
      setAviso(`Enviamos um link de confirmação para ${email}. Abra-o para ativar a conta.`);
    }
    // Com sessão criada, quem troca de tela é o observador de sessão do App.
  }

  async function comGoogle(): Promise<void> {
    setEnviando(true);
    setErro(null);
    const resultado = await entrarComGoogle();
    // Em caso de sucesso o navegador sai da página; só voltamos aqui se
    // deu errado, e aí o botão precisa voltar a funcionar.
    setEnviando(false);
    if (!resultado.ok) setErro(resultado.mensagem ?? 'Não foi possível continuar.');
  }

  function alternarModo(): void {
    setModo(cadastrando ? 'entrar' : 'cadastrar');
    setErro(null);
    setAviso(null);
  }

  return (
    <div className="grow grid lg:grid-cols-2">
      {/* Painel visual: some no celular, onde a coluna do formulário ocupa
          a tela inteira e o tabuleiro só empurraria o campo para baixo. */}
      <aside aria-hidden="true" className="hidden lg:block">
        <PainelTabuleiro />
      </aside>

      <div className="flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <button type="button" onClick={onVoltar} className="btn btn-ghost !px-3 !py-1.5 mb-8">
            <ArrowLeft className="size-4" aria-hidden="true" />
            Voltar
          </button>

          <h1 className="font-bold" style={{ fontSize: 'var(--text-display)', lineHeight: 1.1 }}>
            {cadastrando ? 'Criar conta' : 'Entrar na mesa'}
          </h1>

          <p className="mt-3 text-muted-foreground" style={{ fontSize: 'var(--text-lead)' }}>
            {cadastrando ? (
              <>
                Para jogar valendo dinheiro.{' '}
                <button
                  type="button"
                  onClick={alternarModo}
                  className="font-semibold underline underline-offset-4"
                  style={{ color: 'var(--primary)' }}
                >
                  Já tenho conta
                </button>
              </>
            ) : (
              <>
                Ainda não tem conta?{' '}
                <button
                  type="button"
                  onClick={alternarModo}
                  className="font-semibold underline underline-offset-4"
                  style={{ color: 'var(--primary)' }}
                >
                  Criar agora
                </button>
              </>
            )}
          </p>

          <button
            type="button"
            className="btn btn-ghost !py-3 mt-8 w-full justify-center gap-3"
            disabled={enviando}
            onClick={() => void comGoogle()}
          >
            <GoogleIcone />
            Continuar com Google
          </button>

          <div className="flex items-center gap-3 my-6" aria-hidden="true">
            <span className="h-px grow" style={{ background: 'var(--border)' }} />
            <span className="rotulo">ou</span>
            <span className="h-px grow" style={{ background: 'var(--border)' }} />
          </div>

          <form onSubmit={enviarFormulario} className="grid gap-5">
            {cadastrando ? (
              <label className="grid gap-1.5">
                <span className="rotulo">Nome na mesa</span>
                <input
                  type="text"
                  name="nome"
                  required
                  maxLength={24}
                  autoComplete="nickname"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Como quer ser chamado"
                  className="w-full rounded-lg border px-4 py-3 outline-none transition"
                  style={{ background: 'var(--card)' }}
                />
                <span className="text-xs text-muted-foreground">
                  É o que o adversário vê. Pode trocar depois.
                </span>
              </label>
            ) : null}

            <label className="grid gap-1.5">
              <span className="rotulo">E-mail</span>
              <input
                type="email"
                name="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@exemplo.com"
                className="w-full rounded-lg border px-4 py-3 outline-none transition"
                style={{ background: 'var(--card)' }}
              />
            </label>

            <label className="grid gap-1.5">
              <span className="rotulo">Senha</span>
              <div className="relative">
                <input
                  type={mostrarSenha ? 'text' : 'password'}
                  name="senha"
                  required
                  minLength={6}
                  autoComplete={cadastrando ? 'new-password' : 'current-password'}
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  placeholder="pelo menos 6 caracteres"
                  className="w-full rounded-lg border px-4 py-3 pr-12 outline-none transition"
                  style={{ background: 'var(--card)' }}
                />
                <button
                  type="button"
                  onClick={() => setMostrarSenha(!mostrarSenha)}
                  aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 grid place-items-center size-9 rounded-md hover:bg-[var(--muted)]"
                >
                  {mostrarSenha ? (
                    <EyeOff className="size-5 text-muted-foreground" aria-hidden="true" />
                  ) : (
                    <Eye className="size-5 text-muted-foreground" aria-hidden="true" />
                  )}
                </button>
              </div>
            </label>

            {cadastrando ? (
              <>
                <label className="grid gap-1.5">
                  <span className="rotulo">Data de nascimento</span>
                  <input
                    type="date"
                    name="nascimento"
                    required
                    max={maiorIdadeEm()}
                    value={nascimento}
                    onChange={(e) => setNascimento(e.target.value)}
                    className="w-full rounded-lg border px-4 py-3 outline-none transition"
                    style={{ background: 'var(--card)' }}
                  />
                  <span className="text-xs text-muted-foreground">
                    A plataforma só aceita maiores de 18 anos.
                  </span>
                </label>

                <label className="grid gap-1.5">
                  <span className="rotulo">CPF</span>
                  <input
                    type="text"
                    name="cpf"
                    required
                    inputMode="numeric"
                    autoComplete="off"
                    value={cpf}
                    onChange={(e) => setCpf(formatarCpf(e.target.value))}
                    placeholder="000.000.000-00"
                    aria-invalid={cpfErrado}
                    className="w-full rounded-lg border px-4 py-3 outline-none transition"
                    style={{
                      background: 'var(--card)',
                      borderColor: cpfErrado ? 'var(--danger)' : undefined,
                    }}
                  />
                  <span
                    className="text-xs"
                    style={{ color: cpfErrado ? 'var(--danger)' : 'var(--muted-foreground)' }}
                  >
                    {cpfErrado
                      ? 'Esse CPF não confere. Verifique os números.'
                      : 'Uma conta por pessoa. Serve para o saque voltar para você, e só para isso.'}
                  </span>
                </label>
              </>
            ) : null}

            {erro ? (
              <p role="alert" className="text-sm font-medium" style={{ color: 'var(--danger)' }}>
                {erro}
              </p>
            ) : null}

            {aviso ? (
              <p role="status" className="text-sm font-medium" style={{ color: 'var(--primary)' }}>
                {aviso}
              </p>
            ) : null}

            <button type="submit" className="btn btn-primary !py-3" disabled={!podeEnviar}>
              {enviando ? 'Um instante…' : cadastrando ? 'Criar conta' : 'Entrar'}
            </button>
          </form>

          <p className="mt-8 text-muted-foreground" style={{ fontSize: 'var(--text-small)' }}>
            {cadastrando
              ? 'Seus dados servem para confirmar que você é maior de 18 anos e para o saque voltar ao seu próprio CPF. Não aparecem para nenhum outro jogador.'
              : 'Xadrez, damas e jogo da velha são jogos de habilidade: quem joga melhor ganha.'}
          </p>
        </div>
      </div>
    </div>
  );
}
